<template>
    <v-app>
        <NavBar v-show="!hide" />
        <SideBar v-show="!hide" />

        <v-main :style="mainStyle">
            <router-view />
        </v-main>
        <UpdateApp />
        <statusSnackbar />
        <BottomNav class="mt-12 pt-2" v-show="!hide" />
    </v-app>
</template>

<script>
import SideBar from './components/SideBar/SideBar.vue';
import NavBar from './components/NavBar/NavBar.vue';
import BottomNav from './components/BottomNav/BottomNav.vue';
import UpdateApp from './UpdateApp.vue';
import { UNLOGGED_ROUTES, AUTH_ROUTES } from './main.js';

const DARK_MODE_KEY = 'app_dark_mode';

export default {
    name: 'App',

    components: {
        SideBar,
        NavBar,
        StatusSnackbar: () =>
            import(
                /* webpackPrefetch: true */ './components/StatusSnackbar/StatusSnackbar.vue'
            ),
        BottomNav,
        UpdateApp,
    },

    data: () => ({}),

    computed: {
        theme() {
            return this.$vuetify.theme.dark ? 'dark' : 'light';
        },
        hide() {
            if (!this.$route?.name) return true;
            const NOT_NAVBAR_ROUTES = new Set([
                ...AUTH_ROUTES,
                ...UNLOGGED_ROUTES,
            ]);
            return NOT_NAVBAR_ROUTES.has(this.$route.name);
        },
        mainStyle() {
            return {
                background: this.$vuetify.theme.dark
                    ? 'var(--bg-dark)'
                    : 'var(--bg-light)',
                transition: 'background 0.4s ease',
            };
        },
    },

    created() {
        const saved = localStorage.getItem(DARK_MODE_KEY);
        if (saved !== null) {
            this.$vuetify.theme.dark = saved === 'true';
        } else {
            // Respect system preference on first visit
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            this.$vuetify.theme.dark = prefersDark;
        }
    },
};
</script>

<style>
/* ─── Design tokens ─────────────────────────────────────────────────── */
:root {
    --bg-light: #f4f6fb;
    --bg-dark: #282828;

    --accent:        #ff3d3d;
    --accent-hover:  #d42a2a;
    --accent-glow:   rgba(24, 24, 24, 0.28);

    --radius-card:  14px;
    --radius-btn:   10px;

    --shadow-sm:  0 1px 4px rgba(0,0,0,.07), 0 2px 12px rgba(0,0,0,.05);
    --shadow-md:  0 4px 20px rgba(0,0,0,.10), 0 1px 4px rgba(0,0,0,.06);
    --shadow-fab: 0 4px 18px rgba(164, 164, 164, 0.35), 0 2px 6px rgba(0,0,0,.15);
}

/* ─── Global resets ─────────────────────────────────────────────────── */
#app {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-family: 'Roboto', 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
    font-weight: 300 !important;
    letter-spacing: -0.01em;
}

/* ─── Cards ─────────────────────────────────────────────────────────── */
.v-card {
    border-radius: var(--radius-card) !important;
    box-shadow: var(--shadow-sm) !important;
    transition: box-shadow 0.25s ease, transform 0.2s ease !important;
}

/* ─── Buttons ────────────────────────────────────────────────────────── */
.v-btn {
    border-radius: var(--radius-btn) !important;
    text-transform: none !important;
    font-weight: 600 !important;
    letter-spacing: 0 !important;
    transition: all 0.2s ease !important;
}

.v-btn.v-btn--contained.primary,
.v-btn.v-btn--contained.accent {
    box-shadow: 0 2px 10px var(--accent-glow) !important;
}

.v-btn.v-btn--contained.primary:hover {
    box-shadow: 0 4px 18px var(--accent-glow) !important;
    transform: translateY(-1px) !important;
}

/* ─── Dialogs ────────────────────────────────────────────────────────── */
.v-dialog {
    border-radius: var(--radius-card) !important;
    -ms-overflow-style: none !important;
    scrollbar-width: none !important;
}

.v-dialog::-webkit-scrollbar {
    display: none !important;
}

/* ─── Inputs ─────────────────────────────────────────────────────────── */
.v-text-field--outlined fieldset {
    border-radius: 10px !important;
    transition: border-color 0.2s ease !important;
}

.v-text-field--outlined:not(.v-input--is-focused):hover fieldset {
    border-color: var(--accent) !important;
}

/* ─── Chips ──────────────────────────────────────────────────────────── */
.v-chip {
    border-radius: 8px !important;
    font-weight: 600 !important;
    font-size: 0.75rem !important;
    letter-spacing: 0 !important;
}

/* ─── Lists ──────────────────────────────────────────────────────────── */
.v-list-item {
    border-radius: 8px !important;
    transition: background 0.15s ease !important;
}

/* ─── Navigation drawer ─────────────────────────────────────────────── */
.v-navigation-drawer {
    border-right: none !important;
    box-shadow: 2px 0 24px rgba(0,0,0,.07) !important;
}

/* ─── Snackbar ───────────────────────────────────────────────────────── */
.v-snack__wrapper {
    border-radius: 12px !important;
    box-shadow: var(--shadow-md) !important;
}

/* ─── Scrollbar ──────────────────────────────────────────────────────── */
::-webkit-scrollbar {
    width: 3px;
    height: 3px;
}

::-webkit-scrollbar-track {
    background: transparent;
}

::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, var(--accent), #6b8fff);
    border-radius: 99px;
}

::-webkit-scrollbar-thumb:hover {
    background: var(--accent-hover);
}

/* ─── Autofill ───────────────────────────────────────────────────────── */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
    -webkit-background-clip: text;
    transition: background-color 5000s ease-in-out 0s;
    box-shadow: inset 0 0 20px 20px transparent;
}

/* ─── Utility ────────────────────────────────────────────────────────── */
.test {
    border-width: 1px !important;
    border-color: rgba(0, 0, 0, 0.08) !important;
    height: 100%;
}

/* ─── Mobile ─────────────────────────────────────────────────────────── */
@media (max-width: 600px) {
    body {
        overflow: hidden !important;
        max-height: 100vh !important;
        overflow-y: scroll !important;
    }

    .dark-mode-fab {
        bottom: 72px !important;
        right: 14px !important;
    }
}

/* ─── Smooth theme transition ────────────────────────────────────────── */
.v-application,
.v-card,
.v-toolbar,
.v-navigation-drawer,
.v-bottom-navigation,
.v-list,
.v-list-item {
    transition:
        background-color 0.35s ease,
        color 0.25s ease,
        border-color 0.25s ease !important;
}

/* ─── Snackbar ───────────────────────────────────────────────────────── */
.v-snack__wrapper {
    border-radius: 14px !important;
    backdrop-filter: blur(18px) saturate(160%) !important;
    -webkit-backdrop-filter: blur(18px) saturate(160%) !important;
    background: rgba(255, 255, 255, 0.55) !important;
    border: 1px solid rgba(255, 255, 255, 0.75) !important;
    box-shadow:
        0 8px 32px rgba(0, 0, 0, 0.10),
        0 2px 8px rgba(0, 0, 0, 0.06),
        inset 0 1px 0 rgba(255, 255, 255, 0.9) !important;
    color: #1a1a2e !important;
}

/* Dark mode variant */
.theme--dark .v-snack__wrapper {
    background: rgba(30, 34, 53, 0.60) !important;
    border-color: rgba(255, 255, 255, 0.10) !important;
    box-shadow:
        0 8px 32px rgba(0, 0, 0, 0.30),
        0 2px 8px rgba(0, 0, 0, 0.20),
        inset 0 1px 0 rgba(255, 255, 255, 0.08) !important;
    color: #e8eaf0 !important;
}

/* Error snackbar */
.v-snack__wrapper.error,
.v-snack--active.error .v-snack__wrapper {
    background: rgba(255, 61, 61, 0.13) !important;
    border-color: rgba(255, 61, 61, 0.28) !important;
    box-shadow:
        0 8px 32px rgba(255, 61, 61, 0.12),
        0 2px 8px rgba(0, 0, 0, 0.06),
        inset 0 1px 0 rgba(255, 255, 255, 0.7) !important;
}

/* Action button */
.v-snack__action .v-btn {
    color: var(--accent) !important;
    font-weight: 700 !important;
    letter-spacing: 0.04em !important;
}

</style>